import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';

import { useHistory } from 'react-router-dom';
import { FaUsers } from 'react-icons/fa';
import { MdSearch } from 'react-icons/md';
import { IPaginate } from 'shared/interfaces';
import { useToast } from 'contexts';
import { ModalHandles } from 'components/Modal';

import {
  getClientes,
  deleteCliente,
  enableCliente,
} from 'services/api/cliente';

import * as C from 'components';
import * as S from './styles';

import { ICliente } from '../interfaces';

export const ClienteList: React.FC = () => {
  const { addToast } = useToast();
  const history = useHistory();
  const [clientes, setClientes] = useState<IPaginate<ICliente>>();
  const [clienteAtual, setClienteAtual] = useState<ICliente>();
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');
  const modalRef = useRef<ModalHandles>(null);
  const [modalType, setModalType] = useState('remove');

  const handleSearch = useCallback(
    ({ page, per_page }) => {
      setLoading(true);

      const params = {
        page,
        per_page,
        term,
      };

      getClientes(params)
        .then((data) => {
          setClientes(data);

          setLoading(false);
        })
        .catch((e) => {
          addToast({
            type: 'error',
            title: 'Ops, Erro',
            description: e.response?.data?.message,
          });

          setLoading(false);
        });
    },
    [addToast, term],
  );

  useEffect(() => {
    const params = {
      page: 1,
      per_page: 20,
    };

    handleSearch(params);
  }, [handleSearch]);

  const handleRemove = useCallback(async () => {
    try {
      await deleteCliente(clienteAtual!.id);

      if (!clientes) return;

      const newList = clientes.data.filter(
        (item) => item.id !== clienteAtual?.id,
      );
      setClientes({ ...clientes, data: newList });

      modalRef.current?.closeModal();

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Registro removido com sucesso',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Ops, Erro',
        description: 'Ocorreu um erro ao remover o registro',
      });
    }
  }, [addToast, clienteAtual, clientes]);

  const handleOpenModal = useCallback(
    (clienteSelected: ICliente, type: string): void => {
      setModalType(type);

      setClienteAtual(clienteSelected);

      modalRef.current?.openModal();
    },
    [],
  );

  const handleEnable = useCallback(() => {
    try {
      enableCliente(clienteAtual!.id)
        .then((c) => {
          if (clientes && c) {
            const newClientes = clientes.data.map((item) => {
              if (item.id === clienteAtual?.id) {
                if (item.enable_app === 'N') {
                  return { ...item, enable_app: 'S' };
                }

                return { ...item, enable_app: 'N' };
              }

              return item;
            });

            setClientes({ ...clientes, data: newClientes });
          }
        })
        .catch((e) => {
          addToast({
            type: 'error',
            title: 'Ops, Erro',
            description: e.response?.data?.message,
          });

          modalRef.current?.closeModal();
        });
    } catch (error) {
      modalRef.current?.closeModal();
    } finally {
      modalRef.current?.closeModal();
    }
  }, [addToast, clienteAtual, clientes]);

  const renderModal = useMemo(() => {
    if (modalType === 'remove') {
      return (
        <C.Modal ref={modalRef} options={{ width: '400px' }}>
          <C.ModalHeader>
            <C.ModalTitle>Você Confirma a EXCLUSÃO?</C.ModalTitle>
          </C.ModalHeader>
          <C.ModalBody>
            <p>O registro será removido completamente!</p>
          </C.ModalBody>
          <C.ModalFooter>
            <C.Position align="right">
              <C.Button
                variant="outline-secondary"
                onClick={() => modalRef.current?.closeModal()}
              >
                Cancelar
              </C.Button>
              <C.Button
                variant="primary"
                onClick={() => handleRemove()}
                style={{ marginLeft: '16px' }}
              >
                SIM, confirmo a exclusão
              </C.Button>
            </C.Position>
          </C.ModalFooter>
        </C.Modal>
      );
    }

    if (modalType === 'status') {
      return (
        <C.Modal ref={modalRef} options={{ width: '400px' }}>
          <C.ModalHeader>
            <C.ModalTitle>
              {clienteAtual?.enable_app === 'S'
                ? 'Confirma desativação do cliente para uso do Mobile?'
                : 'Confirma ativação do cliente para uso do Mobile?'}
            </C.ModalTitle>
          </C.ModalHeader>
          <C.ModalBody>
            <p>
              {clienteAtual?.enable_app === 'S'
                ? 'O cliente será desabilitado para uso do mobile!'
                : 'O cliente será habilitado para uso do mobile!'}
            </p>
          </C.ModalBody>
          <C.ModalFooter>
            <C.Position align="right">
              <C.Button
                variant="outline-secondary"
                onClick={() => modalRef.current?.closeModal()}
              >
                Cancelar
              </C.Button>
              <C.Button
                variant="primary"
                onClick={() => handleEnable()}
                style={{ marginLeft: '16px' }}
              >
                SIM, confirmo a ativação
              </C.Button>
            </C.Position>
          </C.ModalFooter>
        </C.Modal>
      );
    }

    return <></>;
  }, [handleEnable, handleRemove, modalType]);

  return (
    <S.Container>
      <C.HeaderBackground>
        <C.HeaderPage>
          <C.HeaderInfo title="Clientes" icon={FaUsers} />

          <S.ContainerSearch>
            <C.InputSearch
              title="Digite aqui o que deseja pesquisar"
              icon={MdSearch}
              placeholder="Pesquisar"
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => {
                e.key === 'Enter'
                  ? handleSearch({ page: 1, per_page: clientes?.per_page })
                  : {};
              }}
            />
          </S.ContainerSearch>

          <div>
            <C.Can checkPermission="clientes_create">
              <C.ButtonLink title="Adicionar Cliente" to="/clientes/new">
                Adicionar
              </C.ButtonLink>
            </C.Can>
          </div>
        </C.HeaderPage>
      </C.HeaderBackground>

      <S.Panel>
        {loading ? (
          <C.Loader loading={loading} align="center" message="Carregando..." />
        ) : (
          <>
            <div className="tbl-content">
              <C.Table>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }} className="text-center">
                      Mobile
                    </th>
                    <th className="text-left">Descrição</th>
                    <th className="text-center">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {clientes &&
                    clientes.data.map((item: ICliente) => (
                      <tr key={item.id}>
                        <td className="text-center">
                          <S.Status
                            size={24}
                            enable={String(item.enable_app === 'S')}
                          />
                        </td>
                        <td>{item.nome}</td>
                        <td className="text-center">
                          <C.PopUpMenu>
                            <C.Can checkPermission="clientes_update">
                              <button
                                type="button"
                                onClick={() =>
                                  history.push(`/clientes/edit/${item.id}`)
                                }
                              >
                                <S.IconEdit
                                  title="Editar"
                                  size={24}
                                  color="#707070"
                                />
                                Editar
                              </button>
                            </C.Can>

                            <C.Can checkPermission="clientes_update">
                              <button
                                type="button"
                                onClick={() => handleOpenModal(item, 'status')}
                              >
                                <S.IconEnable
                                  title="Ativar Mobile"
                                  size={24}
                                  color="#707070"
                                />
                                Ativar
                              </button>
                            </C.Can>

                            <C.Can checkPermission="clientes_delete">
                              <button
                                type="button"
                                onClick={() => handleOpenModal(item, 'remove')}
                              >
                                <S.IconDelete
                                  title="Remover"
                                  size={24}
                                  color="#707070"
                                />
                                Remover
                              </button>
                            </C.Can>
                          </C.PopUpMenu>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </C.Table>
            </div>
          </>
        )}
        <S.PaginationContainer>
          {clientes && (
            <C.Pagination
              page={clientes?.page}
              total={clientes?.total}
              per_page={clientes?.per_page}
              last_page={clientes?.last_page}
              onSearch={handleSearch}
            />
          )}
        </S.PaginationContainer>
      </S.Panel>
      {renderModal}
    </S.Container>
  );
};
